import { SupportedLanguage } from 'config';

export class ApiError extends Error {
  statusCode: number;
  errors: string[];

  constructor(path: string, statusCode: number, error: string, errors: string[]) {
    super(error);
    this.statusCode = statusCode;
    this.errors = errors;
    this.message = errors.join('\n');

    console.error(`API Request Failed to`, path);
    console.error(`API Error:  ${statusCode} - ${error}`, errors);
  }
}

export class ApiClient {
  private baseUrl: string;
  private headers: Headers;
  private defaultError: string;
  private token: string | null;
  private language: SupportedLanguage;

  constructor(baseUrl: string, language: SupportedLanguage, defaultError: string, token: string | null) {
    this.baseUrl = baseUrl;
    this.defaultError = defaultError;
    this.token = token;
    this.language = language;

    this.headers = this.getHeaders();
  }

  private getHeaders() {
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Accept-Language', this.language);
    headers.set('Accept', 'application/json');
    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`);
    }

    return headers;
  }

  /**
   * Change the language of the API client
   *
   * @param {SupportedLanguage} language
   */
  changeLanguage(language: SupportedLanguage) {
    this.headers.set('Accept-Language', language);
  }

  /**
   * Add an authorization token to the API client
   *
   * @param {string} token
   */
  addAuthorization(token: string) {
    this.headers.set('Authorization', `Bearer ${token}`);
  }

  /**
   * Remove the authorization token from the API client
   */
  removeAuthorization() {
    this.headers.delete('Authorization');
  }

  /**
   * Handle API errors
   *
   * @param {string} link
   * @param {Response} response
   *
   * @throws {ApiError}
   */
  private async handleErrors(link: string, response: Response) {
    /**
     * Example error response:
     {
      "statusCode": 400,
      "message": [
        "password must be at least 8 characters long, contain at least 1 lowercase letter, 1 uppercase letter, 1 number and 1 symbol",
        "confirmPassword must be at least 8 characters long, contain at least 1 lowercase letter, 1 uppercase letter, 1 number and 1 symbol",
        "email is invalid",
        "language must be in valid values of en, fi, ru"
      ],
      "error": "Bad Request"
    }
    */
    if (!response.ok) {
      const body = await response.json();
      throw new ApiError(
        link,
        response.status,
        response.statusText,
        Array.isArray(body?.message) ? body.message : [body.message]
      );
    }
  }

  /**
   * Make a GET request to the API
   *
   * @param path
   * @param params
   *
   * @returns {Promise<T>}
   *
   * @throws {ApiError}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async get<T>(path: string, queryParams: Record<string, any> | null = null): Promise<T> {
    let link = `${this.baseUrl}${path}`;
    if (queryParams && Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams();

      Object.entries(queryParams)
        .filter(([, v]) => v != null)
        .forEach(([k, v]) => {
          if (Array.isArray(v)) {
            // Handle arrays by appending each value with the same key
            v.forEach(item => params.append(k, String(item)));
          } else {
            params.append(k, String(v));
          }
        });

      const query = params.toString();
      if (query) {
        link += `?${query}`;
      }
    }
    try {
      const response = await fetch(link, {
        method: 'GET',
        headers: this.headers
      });

      if (response.ok) {
        return response.json();
      } else {
        await this.handleErrors(path, response);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }

    // This line will never be reached, but it's required to satisfy the return type
    throw new ApiError(path, 500, 'Unexpected Error', [this.defaultError]);
  }

  /**
   * Make a POST request to the API
   *
   * @param path
   * @param body
   *
   * @returns {Promise<T>}
   *
   * @throws {ApiError}
   */
  async post<T>(path: string, body: object): Promise<T> {
    const link = `${this.baseUrl}${path}`;
    try {
      const response = await fetch(link, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(body)
      });

      if (response.ok) {
        return response.json();
      } else {
        await this.handleErrors(path, response);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }

    // This line will never be reached, but it's required to satisfy the return type
    throw new ApiError(path, 500, 'Unexpected Error', [this.defaultError]);
  }

  /**
   * Download a file from the API
   *
   * @param path
   * @param body
   * @param filename
   *
   * @returns {Promise<void>}
   *
   * @throws {ApiError}
   */
  async download(path: string, body: object, filename: string): Promise<void> {
    const link = `${this.baseUrl}${path}`;
    const headers = this.getHeaders();

    headers.set('Accept', 'application/pdf');
    try {
      const response = await fetch(link, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        await this.handleErrors(path, response);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // get the file name from the content-disposition header
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }
  }

  /**
   * Download a file from the API via GET with query params
   *
   * @param path
   * @param queryParams
   * @param filename
   *
   * @returns {Promise<void>}
   *
   * @throws {ApiError}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async downloadGet(path: string, queryParams: Record<string, any> | null, filename: string): Promise<void> {
    let link = `${this.baseUrl}${path}`;
    if (queryParams && Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams();
      Object.entries(queryParams)
        .filter(([, v]) => v != null)
        .forEach(([k, v]) => {
          if (Array.isArray(v)) {
            v.forEach(item => params.append(k, String(item)));
          } else {
            params.append(k, String(v));
          }
        });
      const query = params.toString();
      if (query) {
        link += `?${query}`;
      }
    }

    const headers = this.getHeaders();
    headers.set('Accept', 'application/pdf');
    try {
      const response = await fetch(link, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        await this.handleErrors(path, response);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }
  }

  /**
   * Download a file from the API
   *
   * @param path
   * @param body
   *
   * @returns {Promise<Blob>}
   *
   * @throws {ApiError}
   */
  async blob(path: string, body: object = {}): Promise<Blob> {
    const link = `${this.baseUrl}${path}`;
    const headers = this.getHeaders();

    headers.set('Accept', 'application/pdf');
    try {
      const response = await fetch(link, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        await this.handleErrors(path, response);
      }

      return await response.blob();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }
  }

  /**
   * File upload
   *
   * @param path
   * @param file
   *
   * @returns {Promise<T>}
   *
   * @throws {ApiError}
   */
  async upload<T>(path: string, file: File): Promise<T> {
    const link = `${this.baseUrl}${path}`;
    const formData = new FormData();
    formData.append('file', file);

    const headers = this.getHeaders();
    headers.delete('Content-Type');

    try {
      const response = await fetch(link, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      if (response.ok) {
        return response.json();
      } else {
        await this.handleErrors(path, response);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }

    // This line will never be reached, but it's required to satisfy the return type
    throw new ApiError(path, 500, 'Unexpected Error', [this.defaultError]);
  }

  /**
   * Make a PATCH request to the API
   *
   * @param path
   * @param body
   *
   * @returns {Promise<T>}
   *
   * @throws {ApiError}
   */
  async patch<T>(path: string, body: object): Promise<T> {
    const link = `${this.baseUrl}${path}`;
    try {
      const response = await fetch(link, {
        method: 'PATCH',
        headers: this.headers,
        body: JSON.stringify(body)
      });

      if (response.ok) {
        return response.json();
      } else {
        await this.handleErrors(path, response);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }

    // This line will never be reached, but it's required to satisfy the return type
    throw new ApiError(path, 500, 'Unexpected Error', [this.defaultError]);
  }

  /**
   * Make a PUT request to the API
   *
   * @param path
   * @param body
   *
   * @returns {Promise<T>}
   *
   * @throws {ApiError}
   */
  async put<T>(path: string, body: object): Promise<T> {
    const link = `${this.baseUrl}${path}`;
    try {
      const response = await fetch(link, {
        method: 'PUT',
        headers: this.headers,
        body: JSON.stringify(body)
      });

      if (response.ok) {
        return response.json();
      } else {
        await this.handleErrors(path, response);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }

    // This line will never be reached, but it's required to satisfy the return type
    throw new ApiError(path, 500, 'Unexpected Error', [this.defaultError]);
  }

  /**
   * Make a DELETE request to the API
   *
   * @param path
   *
   * @returns {Promise<void>}
   *
   * @throws {ApiError}
   */
  async delete(path: string): Promise<void> {
    const link = `${this.baseUrl}${path}`;
    try {
      const response = await fetch(link, {
        method: 'DELETE',
        headers: this.headers
      });

      if (!response.ok) {
        await this.handleErrors(path, response);
      }

      return;
    } catch (error) {
      console.error(error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }
  }

  /**
   * Make a DELETE request to the API that returns the updated entity
   *
   * @param path
   *
   * @returns {Promise<T>}
   *
   * @throws {ApiError}
   */
  async deleteWithResponse<T>(path: string): Promise<T> {
    const link = `${this.baseUrl}${path}`;
    try {
      const response = await fetch(link, {
        method: 'DELETE',
        headers: this.headers
      });

      if (response.ok) {
        return response.json();
      } else {
        await this.handleErrors(path, response);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(path, 400, 'Request Failed', [this.defaultError]);
    }

    // This line will never be reached, but it's required to satisfy the return type
    throw new ApiError(path, 500, 'Unexpected Error', [this.defaultError]);
  }
}
